import { IBudgetService } from '../types';

// ============================================================
// PARSERS
// ============================================================

export const parseQuantity = (quantity: string | number): number => {
  if (typeof quantity === 'number') return quantity;

  let clean = String(quantity).replace(/[^\d.,]/g, '');
  clean = clean.replace(',', '.');
  const parsed = Number.parseFloat(clean);

  return Number.isNaN(parsed) ? 1 : parsed;
};

export const parseQuantityToNumber = (qty: string): number => {
  const clean = qty.replace(/[^\d.,]/g, '').replace(',', '.');
  return Number.parseFloat(clean) || 0;
};

export const parseDiscountPercent = (discount: string): number => {
  let clean = discount.replace(/[^\d.,]/g, '').replace(',', '.');
  const parsed = Number.parseFloat(clean);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(100, parsed));
};

export const parseNumber = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;

  let clean = String(value).replace(/[^\d.,-]/g, '');

  if (clean.includes(',') && clean.includes('.')) {
    clean = clean.replace(/\./g, '').replace(',', '.');
  } else {
    clean = clean.replace(',', '.');
  }

  const parsed = Number.parseFloat(clean);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const parseHourMeter = (hourmeter: string | number | null | undefined): number | null => {
  if (hourmeter == null) return null;
  if (typeof hourmeter === 'number') return hourmeter;

  const parts = String(hourmeter).split(':');

  if (parts.length === 3) {
    const h = Number.parseInt(parts[0] || '0', 10);
    const m = Number.parseInt(parts[1] || '0', 10);
    const s = Number.parseInt(parts[2] || '0', 10);
    return h * 3600 + m * 60 + s;
  }

  const parsed = Number.parseFloat(String(hourmeter).replace(/[^\d.]/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
};

// ============================================================
// HELPERS
// ============================================================

const round = (value: number, decimals = 2): number => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export const roundMoney = (value: number): number => round(value, 2);

// ============================================================
// CÁLCULO DO ITEM
// ============================================================

const calculateItemValues = (rawItem: any) => {
  const isCovered = rawItem.isCovered === true;
  const itemValue = parseNumber(rawItem.value);
  const quantity = parseQuantity(rawItem.quantity);
  const gross = itemValue * quantity;

  let discountRaw = 0;

  if (!isCovered) {
    if (typeof rawItem.discount === 'number') {
      discountRaw = rawItem.discount;
    } else if (typeof rawItem.discount === 'string') {
      const percent = parseDiscountPercent(rawItem.discount);
      discountRaw = (gross * percent) / 100;
    }
  }

  const subtotalRaw = isCovered ? 0 : gross - discountRaw;

  return {
    itemValue,
    quantity,
    gross,
    discountRaw,
    subtotalRaw,
    discount: roundMoney(discountRaw),
    subtotal: roundMoney(subtotalRaw),
    isCovered,
  };
};

// ============================================================
// CÁLCULO TOTAL
// ============================================================

export const calculateBudgetTotals = (services: IBudgetService[]) => {
  const validServices = services
    .filter((service) => {
      if (service.isExternalService === true || service.serviceType === 'OTHER') {
        return true;
      }
      return service.serviceId && service.serviceId.trim() !== '';
    })
    .map((service) => ({
      ...service,
      items: service.items?.filter((item) => !item.removed) || [],
    }))
    .filter((service) => service.items.length > 0);

  const mappedServices = validServices.map((service) => {
    const serviceItems = service.items.map((item: any) => {
      const calc = calculateItemValues(item);

      return {
        ref: item.ref,
        item: item.item || '',
        description: item.description,
        quantity: calc.quantity,
        value: calc.itemValue,
        discount: roundMoney(calc.discountRaw),
        subtotal: roundMoney(calc.subtotalRaw),
        isCovered: item.isCovered === true,

        _gross: calc.gross,
        _discountRaw: calc.discountRaw,
        _subtotalRaw: calc.subtotalRaw,
      };
    });

    const serviceSubtotalRaw = serviceItems.reduce((s, i) => s + i._subtotalRaw, 0);
    
    const serviceDiscountPercent = 
      (service.volkstotalPercentage ?? 0) +
      (service.dealershipPercentage ?? 0) +
      (service.warrantyPercentage ?? 0);
    const serviceDiscountRaw = (serviceSubtotalRaw * serviceDiscountPercent) / 100;

    return {
      ref: service.ref,
      service: service.isExternalService === true 
        ? (service.serviceName || service.serviceId || 'SERVIÇO EXTERNO')
        : (service.serviceName || service.serviceId),
      serviceType: service.serviceType,
      description: '-',
      items: serviceItems.map(({ _gross, _subtotalRaw, _discountRaw, ...i }) => i),

      percentages: {
        customer: service.clientPercentage ?? 0,
        volkstok: service.volkstotalPercentage ?? 0,
        dealership: service.dealershipPercentage ?? 0,
        warranty: service.warrantyPercentage ?? 0,
      },

      observation: service.observation ?? undefined,

      subtotal: roundMoney(serviceSubtotalRaw),
      discount: roundMoney(serviceDiscountRaw),
      isExternalService: service.isExternalService ?? false,
      failureMode: service.failureModeId,

      _subtotal: serviceSubtotalRaw,
      _discount: serviceDiscountRaw,
    };
  });

  const subtotalRaw = mappedServices.reduce((s, svc) => s + svc._subtotal, 0);
  const discountRaw = mappedServices.reduce((s, svc) => s + svc._discount, 0);
  const netTotalRaw = subtotalRaw - discountRaw;

  return {
    services: mappedServices.map(({ _subtotal, _discount, ...svc }) => svc),
    subtotal: roundMoney(subtotalRaw),
    discount: roundMoney(discountRaw),
    netTotal: roundMoney(netTotalRaw),
  };
};
